import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { AppProvider, useApp } from "@/context/AppContext";
import { toast } from "sonner";

// Pages
import Dashboard from "@/pages/Dashboard";
import Collaborateurs from "@/pages/Collaborateurs";
import CollaborateurDetail from "@/pages/CollaborateurDetail";
import NouveauCollaborateur from "@/pages/NouveauCollaborateur";
import ModifierCollaborateur from "@/pages/ModifierCollaborateur";
import Autorisations from "@/pages/Autorisations";
import CACES from "@/pages/CACES";
import Formations from "@/pages/Formations";
import Materiel from "@/pages/Materiel";
import Demandes from "@/pages/Demandes";
import DemandeDetail from "@/pages/DemandeDetail";
import NouvelleAutorisation from "@/pages/NouvelleAutorisation";
import GestionRoles from "@/pages/GestionRoles";
import MaSignature from "@/pages/MaSignature";
import GestionCategoriesCACES from "@/pages/GestionCategoriesCACES";
import GestionTypesVisites from "@/pages/GestionTypesVisites";
import GestionFormations from "@/pages/GestionFormations";
import GestionMateriel from "@/pages/GestionMateriel";
import GestionTypesMateriel from "@/pages/GestionTypesMateriel";
import GestionInvitations from "@/pages/GestionInvitations";
import GestionModeles from "@/pages/GestionModeles";
import GestionSecteurs from "@/pages/GestionSecteurs";
import GestionVisitesMedicales from "@/pages/GestionVisitesMedicales";
import PlanningFormations from "@/pages/PlanningFormations";
import SessionsFormationCACES from "@/pages/SessionsFormationCACES";
import Login from "@/pages/Login";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ResetPassword from "@/pages/ResetPassword";
import VerifyDocument from "@/pages/VerifyDocument";
import MonProfil from "@/pages/MonProfil";
import MaterielChecklist from "@/pages/MaterielChecklist";
import PolitiqueConfidentialite from "@/pages/PolitiqueConfidentialite";
import ParametresSysteme from "@/pages/ParametresSysteme";
import GestionCompetences from "@/pages/GestionCompetences";
import CompetencesCollaborateur from "@/pages/CompetencesCollaborateur";
import VerificationCompetences from "@/pages/VerificationCompetences";
import GestionMachinesCompetences from "@/pages/GestionMachinesCompetences";
import DemandesEPI from "@/pages/DemandesEPI";
import NouvellDemandeEPI from "@/pages/NouvellDemandeEPI";
import DemandeEPIDetail from "@/pages/DemandeEPIDetail";
import DashboardEPI from "@/pages/DashboardEPI";
import GestionTypesEPI from "@/pages/GestionTypesEPI";

// Components
import CookieConsent from "@/components/CookieConsent";

// Configuration des rôles autorisés par route
const ROUTE_PERMISSIONS = {
  '/': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager', 'Collaborateur', 'Maintenance'],
  '/collaborateurs': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager'],
  '/autorisations': ['Admin', 'HSE', 'Directeur', 'Maintenance', 'Manager'],
  '/caces': ['Admin', 'HSE', 'Directeur', 'RH', 'Manager'],
  '/formations': ['Admin', 'HSE', 'RH', 'Manager'],
  '/visites-medicales': ['Admin', 'HSE', 'Directeur', 'RH'],
  '/materiel': ['Admin', 'HSE', 'Manager', 'Maintenance'],
  '/demandes': ['Admin', 'HSE', 'Directeur', 'Manager'],
  '/nouvelle-autorisation': ['Admin', 'HSE', 'Manager'],
  '/planning-formations': ['Admin', 'HSE', 'RH', 'Manager'],
  '/sessions': ['Admin', 'HSE'],
  '/competences': ['Admin', 'HSE', 'RH', 'Manager'],
  '/machines-competences': ['Admin', 'HSE', 'RH', 'Manager'],
  // EPI Spécifiques routes
  '/epi/demandes': ['Admin', 'HSE', 'Directeur', 'Manager', 'Maintenance'],
  '/epi/nouvelle-demande': ['Admin', 'HSE', 'Manager'],
  '/epi/dashboard': ['Admin', 'HSE', 'Directeur', 'Manager', 'Maintenance'],
  '/epi/types': ['Admin', 'HSE'],
  // Settings routes
  '/roles': ['Admin'],
  '/gestion-roles': ['Admin'],
  '/invitations': ['Admin'],
  '/secteurs': ['Admin', 'HSE'],
  '/categories-caces': ['Admin', 'HSE'],
  '/types-visites': ['Admin', 'RH', 'HSE'],
  '/gestion-formations': ['Admin', 'HSE', 'RH', 'Manager'],
  '/gestion-materiel': ['Admin', 'HSE'],
  '/types-materiel': ['Admin', 'HSE'],
  '/gestion-modeles': ['Admin', 'HSE'],
};

// Protected Route component (authentication only)
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useApp();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

// Role-Protected Route component (authentication + role check)
function RoleProtectedRoute({ children, allowedRoles, path }) {
  const { isAuthenticated, loading, currentUser } = useApp();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check role permission
  const userRole = currentUser?.role;
  const hasPermission = allowedRoles.includes(userRole) || userRole === 'Admin';
  
  if (!hasPermission) {
    // Show error and redirect to dashboard
    toast.error("Accès non autorisé à cette page");
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes (without Layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/invitation" element={<AcceptInvitation />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ResetPassword />} />
      <Route path="/verify/:docType/:verificationCode" element={<VerifyDocument />} />
      <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
      
      {/* Protected routes with role-based access */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      
      {/* Collaborateurs - Admin, HSE, Directeur, RH, Manager */}
      <Route path="/collaborateurs" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'RH', 'Manager']}>
          <Collaborateurs />
        </RoleProtectedRoute>
      } />
      <Route path="/collaborateurs/nouveau" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <NouveauCollaborateur />
        </RoleProtectedRoute>
      } />
      <Route path="/collaborateurs/:id" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'RH', 'Manager']}>
          <CollaborateurDetail />
        </RoleProtectedRoute>
      } />
      <Route path="/collaborateurs/:id/modifier" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <ModifierCollaborateur />
        </RoleProtectedRoute>
      } />
      
      {/* Autorisations - Admin, HSE, Directeur, Maintenance, Manager */}
      <Route path="/autorisations" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Maintenance', 'Manager']}>
          <Autorisations />
        </RoleProtectedRoute>
      } />
      
      {/* CACES - Admin, HSE, Directeur, RH, Manager (documents personnels) */}
      <Route path="/caces" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'RH', 'Manager']}>
          <CACES />
        </RoleProtectedRoute>
      } />
      
      {/* Formations - Admin, HSE, RH, Manager */}
      <Route path="/formations" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <Formations />
        </RoleProtectedRoute>
      } />
      
      {/* Visites Médicales - Admin, HSE, Directeur, RH (documents personnels) */}
      <Route path="/visites-medicales" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'RH']}>
          <GestionVisitesMedicales />
        </RoleProtectedRoute>
      } />
      
      {/* Matériel - Admin, HSE, Manager, Maintenance */}
      <Route path="/materiel" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Manager', 'Maintenance']}>
          <Materiel />
        </RoleProtectedRoute>
      } />
      <Route path="/materiel/:id/checklist" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Manager', 'Maintenance', 'Directeur']}>
          <MaterielChecklist />
        </RoleProtectedRoute>
      } />
      
      {/* Demandes - Admin, HSE, Directeur */}
      <Route path="/demandes" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Manager']}>
          <Demandes />
        </RoleProtectedRoute>
      } />
      <Route path="/demandes/:id" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Manager']}>
          <DemandeDetail />
        </RoleProtectedRoute>
      } />
      
      {/* Nouvelle autorisation - Admin, HSE, Manager */}
      <Route path="/nouvelle-autorisation" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Manager']}>
          <NouvelleAutorisation />
        </RoleProtectedRoute>
      } />
      
      {/* Planning formations - Admin, HSE, RH, Manager */}
      <Route path="/planning-formations" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <PlanningFormations />
        </RoleProtectedRoute>
      } />
      
      {/* Sessions - Admin, HSE */}
      <Route path="/sessions" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <SessionsFormationCACES />
        </RoleProtectedRoute>
      } />
      
      {/* Settings - Role-based access */}
      <Route path="/roles" element={
        <RoleProtectedRoute allowedRoles={['Admin']}>
          <GestionRoles />
        </RoleProtectedRoute>
      } />
      <Route path="/gestion-roles" element={
        <RoleProtectedRoute allowedRoles={['Admin']}>
          <GestionRoles />
        </RoleProtectedRoute>
      } />
      <Route path="/ma-signature" element={<ProtectedRoute><MaSignature /></ProtectedRoute>} />
      <Route path="/mon-profil" element={<ProtectedRoute><MonProfil /></ProtectedRoute>} />
      <Route path="/categories-caces" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionCategoriesCACES />
        </RoleProtectedRoute>
      } />
      <Route path="/types-visites" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'RH', 'HSE']}>
          <GestionTypesVisites />
        </RoleProtectedRoute>
      } />
      <Route path="/gestion-formations" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <GestionFormations />
        </RoleProtectedRoute>
      } />
      <Route path="/gestion-materiel" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionMateriel />
        </RoleProtectedRoute>
      } />
      <Route path="/types-materiel" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionTypesMateriel />
        </RoleProtectedRoute>
      } />
      <Route path="/invitations" element={
        <RoleProtectedRoute allowedRoles={['Admin']}>
          <GestionInvitations />
        </RoleProtectedRoute>
      } />
      <Route path="/gestion-modeles" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionModeles />
        </RoleProtectedRoute>
      } />
      <Route path="/secteurs" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionSecteurs />
        </RoleProtectedRoute>
      } />
      <Route path="/competences" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <GestionCompetences />
        </RoleProtectedRoute>
      } />
      <Route path="/collaborateurs/:id/competences" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'RH', 'Manager', 'HSE', 'Directeur']}>
          <CompetencesCollaborateur />
        </RoleProtectedRoute>
      } />
      <Route path="/verification-competences/:id" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'RH', 'Manager', 'HSE', 'Directeur', 'Maintenance', 'Collaborateur']}>
          <VerificationCompetences />
        </RoleProtectedRoute>
      } />
      <Route path="/machines-competences" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'RH', 'Manager']}>
          <GestionMachinesCompetences />
        </RoleProtectedRoute>
      } />

      {/* EPI Spécifiques Routes */}
      <Route path="/epi/demandes" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Manager', 'Maintenance']}>
          <DemandesEPI />
        </RoleProtectedRoute>
      } />
      <Route path="/epi/nouvelle-demande" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Manager']}>
          <NouvellDemandeEPI />
        </RoleProtectedRoute>
      } />
      <Route path="/epi/demandes/:id" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Manager', 'Maintenance']}>
          <DemandeEPIDetail />
        </RoleProtectedRoute>
      } />
      <Route path="/epi/dashboard" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE', 'Directeur', 'Manager', 'Maintenance']}>
          <DashboardEPI />
        </RoleProtectedRoute>
      } />
      <Route path="/epi/types" element={
        <RoleProtectedRoute allowedRoles={['Admin', 'HSE']}>
          <GestionTypesEPI />
        </RoleProtectedRoute>
      } />

      <Route path="/parametres-systeme" element={
        <RoleProtectedRoute allowedRoles={['Admin']}>
          <ParametresSysteme />
        </RoleProtectedRoute>
      } />
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppProvider>
      <div className="App">
        <BrowserRouter>
          <AppRoutes />
          <CookieConsent />
        </BrowserRouter>
        <Toaster />
      </div>
    </AppProvider>
  );
}

export default App;
